import { useMutation, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { RemotePositionRepo } from '@repository/position/PositionRepo';
import { PositionDetailVO } from '@domain/vo/position/jd/PositionDetailVO';
import { PositionCompanyCardListVO } from '@domain/vo/position/jd/PositionCompanyCardListVO';
import { PositionJobListCardVO } from '@domain/vo/position/jd/PositionJobListCardVO';
import { PositionChallengeCardListVO } from '@domain/vo/position/jd/PositionChallengeCardListVO';
import { PositionBookmarkRs } from '@domain/rs/position/jd/PositionBookmarkRs';

const remotePositionRepo = new RemotePositionRepo();

export const jdKeys = ['position', 'jd'];

export const fetchJD = async (positionSn: number | null) => {
  if (!positionSn) return;
  const { data } = await remotePositionRepo.fetchJD(positionSn);
  return new PositionDetailVO(data);
};

export const useJD = (positionSn: number | null) => {
  return useQuery([...jdKeys, positionSn], () => fetchJD(positionSn), {
    enabled: !!positionSn,
    refetchOnWindowFocus: true,
  });
};

export const useJDPreview = (publicKey: string, positionId: string) => {
  return useQuery(
    [...jdKeys, publicKey, positionId],
    async () => {
      const { data } = await remotePositionRepo.fetchJDPreview(publicKey, positionId);
      return new PositionDetailVO(data);
    },
    {
      enabled: !!publicKey && !!positionId,
    },
  );
};

export const useMatchingCompany = (positionSn: number) => {
  return useQuery(
    [...jdKeys, 'matchingCompany', positionSn],
    async () => {
      const { data } = await remotePositionRepo.fetchMatchingCompany(positionSn);
      return new PositionCompanyCardListVO(data);
    },
    {
      enabled: !!positionSn,
    },
  );
};

export const useMatchingJob = (positionSn: number, pageSize: number) => {
  return useQuery(
    [...jdKeys, 'matchingJob', positionSn, pageSize],
    async () => {
      const { data } = await remotePositionRepo.fetchMatchingJob(positionSn, pageSize);
      return new PositionJobListCardVO(data);
    },
    {
      enabled: !!positionSn,
    },
  );
};

export const useMatchingChallenge = (positionSn: number) => {
  return useQuery(
    [...jdKeys, 'matchingChallenge', positionSn],
    async () => {
      const { data } = await remotePositionRepo.fetchMatchingChallenge(positionSn);
      return new PositionChallengeCardListVO(data);
    },
    { enabled: !!positionSn },
  );
};

export const useCancelApplication = (onSuccess: () => void, onError: (e: AxiosError) => void) => {
  return useMutation((positionSn: number) => remotePositionRepo.fetchCancelApplication(positionSn), {
    onSuccess: onSuccess,
    onError: onError,
  });
};

export const useDeleteBookmark = (onSuccess: () => void) => {
  return useMutation((positionSn: number) => remotePositionRepo.fetchDeleteBookmark(positionSn), {
    onSuccess: onSuccess,
  });
};

export const useAddBookmark = (onSuccess: (data: AxiosResponse<PositionBookmarkRs>) => void) => {
  return useMutation((positionSn: number) => remotePositionRepo.fetchAddBookmark(positionSn), {
    onSuccess: (data) => onSuccess(data),
  });
};
